import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { updateBookingStatus } from '../api';
import './HostDashboard.css';
import MessageThread from '../components/MessageThread';
import BlockedDatesCalendar from '../components/BlockedDatesCalendar';

const STATUS_COLOURS = {
  pending:   { bg: 'rgba(196,98,45,0.1)',  color: '#C4622D' },
  confirmed: { bg: 'rgba(74,124,89,0.1)',  color: '#4A7C59' },
  rejected:  { bg: 'rgba(192,57,43,0.1)', color: '#C0392B' },
};

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmt = d => { const dt = new Date(d); return `${dt.getDate()} ${MONTHS_SHORT[dt.getMonth()]} ${dt.getFullYear()}`; };

export default function HostDashboard() {
  const navigate = useNavigate();
  const [user, setUser]               = useState(null);
  const [listings, setListings]       = useState([]);
  const [bookings, setBookings]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('bookings');
  const [bookingFilter, setBookingFilter] = useState('pending');
  const [openThread, setOpenThread] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [availabilityListing, setAvailabilityListing] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) { navigate('/signin'); return; }
      setUser(user);
      loadData(user.id);

      // Load Stripe Connect status
      const { data: profile } = await supabase
        .from('profiles')
        .select('stripe_account_id, stripe_account_status')
        .eq('user_id', user.id)
        .single();
      setStripeStatus(profile?.stripe_account_status || 'not_connected');

      // If returning from Stripe onboarding, re-check status
      if (window.location.search.includes('stripe=connected')) {
        setStripeStatus('pending');
      }
    });
  }, [navigate]);

  const handleConnectStripe = async () => {
    if (!user) return;
    setConnectLoading(true);
    setConnectError(null);
    try {
      const res = await fetch('/api/create-connect-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          email: user.email,
          returnUrl: `${window.location.origin}/dashboard/host`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setConnectError(data.error || 'Could not connect to Stripe. Please try again.');
        setConnectLoading(false);
      }
    } catch (e) {
      setConnectError('Something went wrong. Please try again.');
      setConnectLoading(false);
    }
  };

  const loadData = async (userId) => {
    setLoading(true);
    const { data: props } = await supabase
      .from('properties')
      .select('*')
      .eq('host_id', userId)
      .order('created_at', { ascending: false });
    const propertyIds = (props || []).map(p => p.id);
    const { data: books } = propertyIds.length > 0 ? await supabase
      .from('bookings')
      .select('*, properties(name, city, country, photos)')
      .in('property_id', propertyIds)
      .order('created_at', { ascending: false }) : { data: [] };
    setListings(props || []);
    const bookingData = books || [];
    setBookings(bookingData);
    setLoading(false);

    // Fetch unread counts
    if (bookingData.length > 0) {
      const threadIds = bookingData.map(b => `booking-${b.id}`);
      supabase.from('messages')
        .select('thread_id')
        .in('thread_id', threadIds)
        .eq('read_by_host', false)
        .neq('sender_type', 'host')
        .then(({ data: unread }) => {
          const counts = {};
          (unread || []).forEach(m => {
            counts[m.thread_id] = (counts[m.thread_id] || 0) + 1;
          });
          setUnreadCounts(counts);
        });
    }
  };

  const handleBookingAction = async (bookingId, status) => {
    await updateBookingStatus(bookingId, status);
    if (user) loadData(user.id);
  };

  const filteredBookings = bookings.filter(b => b.status === bookingFilter);
  const counts = {
    pending:   bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    rejected:  bookings.filter(b => b.status === 'rejected').length,
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <div className="dashboard-logo" onClick={() => navigate('/')}>
            <div className="nav-logo-mark">س</div>
            <span className="dashboard-logo-text">SunnaStays</span>
          </div>
          <span className="dashboard-tag">Host Dashboard</span>
        </div>
        <div className="dashboard-header-right">
          <button className="dashboard-nav-btn" onClick={() => navigate('/host')}>+ New listing</button>
          <button className="dashboard-nav-btn" onClick={async () => { await supabase.auth.signOut(); navigate('/'); }}>Sign out</button>
        </div>
      </div>

      <div className="dashboard-body">
        <div className="dashboard-welcome">
          <h1 className="dashboard-title">Host dashboard</h1>
          <p className="dashboard-sub">{user?.email}</p>
        </div>

        <div className="dashboard-stats">
          <div className="dash-stat">
            <div className="dash-stat-num">{listings.length}</div>
            <div className="dash-stat-label">Total listings</div>
          </div>
          <div className="dash-stat">
            <div className="dash-stat-num" style={{color:'var(--terra)'}}>{counts.pending}</div>
            <div className="dash-stat-label">Pending requests</div>
          </div>
          <div className="dash-stat">
            <div className="dash-stat-num" style={{color:'var(--success)'}}>{counts.confirmed}</div>
            <div className="dash-stat-label">Confirmed bookings</div>
          </div>
          <div className="dash-stat">
            <div className="dash-stat-num">
              £{bookings.filter(b => b.status === 'confirmed').reduce((sum, b) => sum + (b.total_price || 0), 0).toLocaleString()}
            </div>
            <div className="dash-stat-label">Total revenue</div>
          </div>
        </div>

        <div className="dashboard-tabs">
          <button className={`dash-tab ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => setActiveTab('bookings')}>
            Booking requests {counts.pending > 0 && <span className="dash-badge">{counts.pending}</span>}
          </button>
          <button className={`dash-tab ${activeTab === 'listings' ? 'active' : ''}`} onClick={() => setActiveTab('listings')}>
            My listings
          </button>
          <button className={`dash-tab ${activeTab === 'payments' ? 'active' : ''}`} onClick={() => setActiveTab('payments')}>
            Payments {stripeStatus !== 'active' && <span className="dash-badge dash-badge--warn">!</span>}
          </button>
        </div>

        {loading ? (
          <div className="dashboard-loading">Loading...</div>
        ) : (
          <>
            {activeTab === 'bookings' && (
              <div>
                <div className="dash-filters">
                  {['pending','confirmed','rejected'].map(s => (
                    <button key={s} className={`dash-filter-btn ${bookingFilter === s ? 'active' : ''}`}
                      onClick={() => setBookingFilter(s)}>
                      {s.charAt(0).toUpperCase()+s.slice(1)} ({counts[s]})
                    </button>
                  ))}
                </div>
                {filteredBookings.length === 0 ? (
                  <div className="dashboard-empty">
                    <div className="dashboard-empty-icon">📋</div>
                    <p>No {bookingFilter} bookings</p>
                  </div>
                ) : (
                  <div className="dash-bookings-list">
                    {filteredBookings.map(b => (
                      <div key={b.id} className="dash-booking-card">
                        <div className="dash-booking-header">
                          <div>
                            <div className="dash-booking-prop">{b.properties?.name}</div>
                            <div className="dash-booking-loc">📍 {b.properties?.city}, {b.properties?.country}</div>
                          </div>
                          <div className="dash-booking-status" style={STATUS_COLOURS[b.status]}>
                            {b.status}
                          </div>
                        </div>
                        <div className="dash-booking-details">
                          <div className="dash-booking-detail"><span>Guest</span><strong>{b.guest_name}</strong></div>
                          <div className="dash-booking-detail"><span>Email</span><strong>{b.guest_email}</strong></div>
                          <div className="dash-booking-detail"><span>Phone</span><strong>{b.guest_phone}</strong></div>
                          <div className="dash-booking-detail"><span>Check-in</span><strong>{fmt(b.checkin)}</strong></div>
                          <div className="dash-booking-detail"><span>Check-out</span><strong>{fmt(b.checkout)}</strong></div>
                          <div className="dash-booking-detail"><span>Guests</span><strong>{b.guests}</strong></div>
                          <div className="dash-booking-detail"><span>Total</span><strong style={{color:'var(--terra)'}}>£{b.total_price}</strong></div>
                          <div className="dash-booking-detail"><span>Requested</span><strong>{fmt(b.created_at)}</strong></div>
                        </div>
                        {b.message && (
                          <div className="dash-booking-message">
                            <span>Message from guest:</span>
                            <p>{b.message}</p>
                          </div>
                        )}
                        {b.status === 'pending' && (
                          <div className="dash-booking-actions">
                            <button className="dash-btn-confirm" onClick={() => handleBookingAction(b.id, 'confirmed')}>✓ Confirm booking</button>
                            <button className="dash-btn-reject" onClick={() => handleBookingAction(b.id, 'rejected')}>✕ Decline</button>
                          </div>
                        )}
                        <button
                          className="guest-message-btn"
                          style={{marginBottom:10}}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); setOpenThread(openThread === b.id ? null : b.id); }}
                        >
                          💬 {openThread === b.id ? 'Close messages' : 'Message guest'}
                          {unreadCounts[`booking-${b.id}`] > 0 && openThread !== b.id && (
                            <span className="msg-badge">{unreadCounts[`booking-${b.id}`]}</span>
                          )}
                        </button>
                        {openThread === b.id && user && (
                          <div style={{marginBottom:14}}>
                            <MessageThread
                              bookingId={b.id}
                              threadId={`booking-${b.id}`}
                              propertyId={b.properties?.id}
                              currentUserId={user.id}
                              currentUserName="Host"
                              senderType="host"
                              otherName={b.guest_name}
                            />
                          </div>
                        )}
                        {b.status === 'confirmed' && (
                          <div className="dash-booking-actions">
                            <button className="dash-btn-reject" onClick={() => handleBookingAction(b.id, 'rejected')}>Cancel booking</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="dash-payments-section">
                <h3 className="dash-payments-title">Payout account</h3>
                <p className="dash-payments-sub">
                  Connect your bank account via Stripe to receive payouts automatically after each stay.
                  SunnaStays takes a 3% host fee — you keep 97% of your nightly rate.
                </p>
                <div className={`dash-stripe-status dash-stripe-status--${stripeStatus}`}>
                  {stripeStatus === 'active' && '✓ Connected — payouts are active'}
                  {stripeStatus === 'pending' && '⏳ Stripe account connected — awaiting verification'}
                  {stripeStatus === 'not_connected' && '⚠ Not connected — you won\'t receive automated payouts'}
                </div>
                {stripeStatus !== 'active' && (
                  <button
                    className="btn-primary dash-stripe-btn"
                    onClick={handleConnectStripe}
                    disabled={connectLoading}
                  >
                    {connectLoading ? 'Connecting…' : stripeStatus === 'pending' ? 'Continue Stripe setup' : 'Connect bank account'}
                  </button>
                )}
                {connectError && (
                  <div style={{color:'#d9534f', fontSize:13, marginBottom:16, padding:'10px 14px', background:'#fdf3f0', borderRadius:10}}>
                    {connectError}
                  </div>
                )}
                <div className="dash-payments-info">
                  <div className="dash-payments-info-row">
                    <span>Your cut</span><strong>97% of nightly rate</strong>
                  </div>
                  <div className="dash-payments-info-row">
                    <span>SunnaStays fee</span><strong>3% of nightly rate</strong>
                  </div>
                  <div className="dash-payments-info-row">
                    <span>Payout timing</span><strong>24 hours after guest check-out</strong>
                  </div>
                  <div className="dash-payments-info-row">
                    <span>Currency</span><strong>GBP</strong>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'listings' && (
              <div>
                {listings.length === 0 ? (
                  <div className="dashboard-empty">
                    <div className="dashboard-empty-icon">🏠</div>
                    <p>No listings yet</p>
                    <button className="btn-primary" onClick={() => navigate('/host')}>List your first property</button>
                  </div>
                ) : (
                  <div className="dash-listings-grid">
                    {listings.map(l => (
                      <div key={l.id} className="dash-listing-card">
                        <div className="dash-listing-img">
                          {l.photos?.[0] ? <img src={l.photos[0]} alt={l.name} /> : <span>🏠</span>}
                        </div>
                        <div className="dash-listing-body">
                          <div className="dash-listing-name">{l.name}</div>
                          <div className="dash-listing-loc">📍 {l.city}, {l.country}</div>
                          <div className="dash-listing-meta">
                            <span>£{l.price}/night</span>
                            <span>{l.bedrooms} bed · {l.max_guests} guests</span>
                          </div>
                          <div className={`dash-listing-status dash-listing-status--${l.status}`}>
                            {l.status === 'approved' ? '✓ Live' : l.status === 'pending' ? '⏳ Pending review' : '✕ Rejected'}
                          </div>
                          {l.status === 'approved' && (
                            <button
                              className="dash-availability-btn"
                              onClick={() => setAvailabilityListing(l)}
                            >
                              Manage availability
                            </button>
                          )}
                          {l.status === 'rejected' && (
                            <div className="dash-rejection-block">
                              {l.rejection_reason && (
                                <div className="dash-rejection-reasons">
                                  <div className="dash-rejection-label">Rejected — reasons:</div>
                                  {l.rejection_reason.split('\n').map((r, i) => (
                                    <div key={i} className="dash-rejection-item">• {r}</div>
                                  ))}
                                </div>
                              )}
                              <button
                                className="dash-resubmit-btn"
                                onClick={() => navigate(`/host?edit=${l.id}`)}
                              >
                                ✏️ Edit &amp; resubmit
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {availabilityListing && (
        <BlockedDatesCalendar
          propertyId={availabilityListing.id}
          propertyName={availabilityListing.name}
          onClose={() => setAvailabilityListing(null)}
        />
      )}
    </div>
  );
}
